import React from "react";
import { formatDateMonthDay } from "../../utils/format";
import { useProjectStatus } from "./useProjectStatus";
import "./ProjectDeltaChip.css";

interface Props {
  /** Active DB version — changing it refetches the status for the new DB.
   *  Mutations refetch automatically via the schedule-mutation event. */
  activeVersion: number | null;
}

/** Signed workday delta → severity bucket for color + label. */
function severityOf(workdaysBehind: number): "behind" | "ahead" | "ontrack" {
  if (workdaysBehind > 0) return "behind";
  if (workdaysBehind < 0) return "ahead";
  return "ontrack";
}

function deltaLabel(workdaysBehind: number): string {
  if (workdaysBehind > 0) return `${workdaysBehind}d behind`;
  if (workdaysBehind < 0) return `${Math.abs(workdaysBehind)}d ahead`;
  return "on track";
}

/**
 * Always-on project-delta indicator in the global header: P6 target finish vs
 * live projected finish, with the signed workday delta. Hidden entirely until
 * both end dates resolve.
 */
const ProjectDeltaChip: React.FC<Props> = ({ activeVersion }) => {
  const status = useProjectStatus(activeVersion);

  if (
    !status ||
    status.plan_end_date == null ||
    status.projected_end_date == null ||
    status.workdays_behind == null
  ) {
    return null;
  }

  const severity = severityOf(status.workdays_behind);

  return (
    <div
      className={`project-delta-chip project-delta-chip--${severity}`}
      title="Project finish: P6 target vs current projection"
    >
      <span className="pdc-seg">
        <span className="pdc-key">Target</span>
        <span className="pdc-date">
          {formatDateMonthDay(status.plan_end_date)}
        </span>
      </span>
      <span className="pdc-sep">→</span>
      <span className="pdc-seg">
        <span className="pdc-key">Projected</span>
        <span className="pdc-date">
          {formatDateMonthDay(status.projected_end_date)}
        </span>
      </span>
      <span className="pdc-delta">{deltaLabel(status.workdays_behind)}</span>
    </div>
  );
};

export default ProjectDeltaChip;
